import { OknoPotwierdzenia } from '@/components/OknoPotwierdzenia';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Boxes, ChevronDown, ChevronRight, Clock, Eraser, Loader2, PauseCircle, Plus, Rows3, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { NowyBebenDialog } from '@/components/NowyBebenDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { ApiError, apiGet } from '@/lib/api';
import { wyrownajGeometrie } from '@/lib/machines-api';
import { opisRozjazdu, zrodloAutomatu } from '@/lib/rozjazd-opis';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';

/**
 * MODUŁY AUTOMATU — bębny (PIC) i ich geometria.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️ PO CO TO JEST. Karta automatu mówiła „bębnów 1" i koniec. Przy pytaniu „ile tu jest
 *  przegród", „jaka to konstrukcja", „dlaczego ta kolumna nie wydaje" trzeba było wejść do SPSUI
 *  na maszynie u klienta — czasem za drabiną.
 *
 *  ⚠️ SUROWY `MODETYPE` ZOSTAJE WIDOCZNY OBOK NAZWY, i to nie jest nadmiar. Nazwy konstrukcji są
 *  NASZYM odczytaniem numerów (zmierzone na testówce: 9 = Rotopoint, 24 = LockerBox,
 *  25 = DynaBox), a nie cytatem z dokumentacji SupplyPointa — takiej nie mamy. To numer, nie nasza
 *  etykieta, jest tym, po czym da się rozmawiać z serwisem.
 *
 *  ⚠️ DWIE LICZBY PRZEGRÓD, NIE JEDNA — „jest" i „konstrukcja obiecuje". Rozjazd jest realny:
 *  małpkomat na testówce ma 611 przegród przy 13 × 54 = 702, bo bęben był po utworzeniu
 *  systemowo poszerzany. Jedna liczba kłamałaby w obie strony.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

type Moduly = {
  /**
   * ROZJAZD MIĘDZY WĘZŁAMI — która maszyna wyspy czego nie ma.
   *
   * ⚠️ TO INNA INFORMACJA NIŻ „konfiguracja mówi o N bębnach, portal zna M". Tamta porównuje
   * portal z maszyną-właścicielem; ta porównuje MASZYNY MIĘDZY SOBĄ. Bez niej rozjazd
   * z 27.08.2026 był widoczny tylko dla kogoś, kto zajrzał do SPSUI na innej maszynie.
   */
  rozjazdy: {
    tabela: string;
    vmid: number;
    wezly: { nodeId: string; wierszy: number; skrot?: string | null }[];
    stabilny: boolean;
    /**
     * CO SIĘ NIE ZGADZA — od tego zależy, czy przycisk wyrównania pomoże.
     *
     * ⚠️ `optional`, bo starsza instancja tego pola nie odda. Brak = rysujemy jak dotąd, czyli
     * same liczby.
     */
    rodzaj?: 'BRAK' | 'LICZBY' | 'TRESC';
  }[];
  /**
   * CZY WYRÓWNANIE GEOMETRII MA TU CO ZROBIĆ — czyli czy KTOKOLWIEK nie ma tego automatu wcale.
   *
   * ⚠️ NIE TO SAMO CO `rozjazdy.length > 0`, i to jest sedno. Wyrównanie idzie akcją `I`, więc
   * zakłada BRAKUJĄCE wiersze i nie prostuje istniejących. Rozjazd „611 kontra 1320" zostawi bez
   * zmian — pokazanie przy nim przycisku obiecywałoby naprawę, której nie ma.
   *
   * ⚠️ `optional`, bo starsza instancja tego pola nie odda, a ekran ma się wtedy narysować.
   */
  doWyrownania?: boolean;
  /**
   * CZY PRZYCISKI ROZSYŁANIA MAJĄ PRAWO COKOLWIEK ZROBIĆ — bramka policzona przez API.
   *
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *  ⚠️⚠️ POWSTAŁO Z AWARII U KLIENTA (Donaldson, 02.09.2026): wyspa w Smart Syncu bez zasianego
   *  chmurowego mastera przyjęła kliknięcie „Uzupełnij pozostałe maszyny" i wpuściła do kolejki
   *  23 operacje z 11 694 zmianami, których nie dało się wykonać — config-down wstrzymuje
   *  wszystko, dopóki nie wie, który węzeł jest masterem. Wyspa wyglądała jak zepsuta.
   *
   *  ⚠️ POWÓD PRZYCHODZI Z API, nie jest tu składany. Ta sama reguła (`wyrownanie-mozliwe.ts`)
   *  odmawia po stronie serwera — dwa własne zdania rozjechałyby się przy pierwszej poprawce.
   *
   *  ⚠️ `optional`, bo starsza instancja tego pola nie odda; wtedy zostaje jak było (przyciski
   *  czynne, odmowa dopiero z API).
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   */
  wyrownanie?: { mozna: boolean; powod: string | null };
  /**
   * KTÓRE TABELE PRZYCISK REALNIE RUSZA — lista z API, nie kopia w kliencie.
   *
   * ⚠️ Czujnik liczy OSIEM tabel per automat, wyrównanie rozsyła SZEŚĆ: `DrawerCompartmentItems`
   * jest patch-only, a `VendingMachineItems` opisuje limit towaru, nie geometrię. Bez tego ekran
   * obiecywałby naprawę wiersza, którego przycisk nie dotyka.
   *
   * ⚠️ `optional`, bo starsza instancja tego nie odda — wtedy nie oznaczamy niczego.
   */
  tabeleWyrownania?: string[];
  /**
   * WĘZEŁ OBSŁUGUJĄCY TEN AUTOMAT — jedyne miejsce, gdzie jego konfiguracja jest PRAWDĄ.
   *
   * ⚠️⚠️ Do 28.08.2026 ekran zgadywał źródło po LICZBIE WIERSZY i mylił się: dla VMID 3 BiPoint ma
   * 15 pozycji, a właściciel 14, bo BiPoint trzyma zjawę z klonowania wyspy — więc baner
   * ogłaszał „pełny obraz ma BiPoint" i „brakuje 1 u właściciela". Miał wszystko na odwrót.
   */
  wlascicielWezel?: string | null;
  /**
   * CO SIĘ DZIEJE Z TYM AUTOMATEM — operacje config-downu w drodze i te, które utknęły.
   *
   * ⚠️⚠️ OKNO „W DRODZE" JEST ZARAZEM OKNEM, W KTÓRYM LICZBY NA TEJ KARCIE MOGĄ POKAZYWAĆ
   * WARTOŚĆ PORTALU, A NIE TĘ Z SPS. Portal świadomie pomija meldunki maszyny o wierszach,
   * na które ma niewykonaną zmianę — bez tej sekcji obok wyglądałoby to jak awaria.
   *
   * ⚠️ Pole opcjonalne, bo starsza instancja go nie odda, a karta ma się wtedy wyświetlić.
   */
  zmiany?: {
    wDrodze: {
      opId: string;
      tytul: string;
      odKiedy: string;
      zmian: number;
      czekaja: string[];
      wstrzymane: { nodeId: string; wymagana: string }[];
    }[];
    odrzucone: { opId: string; tytul: string; nodeId: string; powod: string; kiedy: string }[];
  };
  stabilneMinut: number;
  automat: { id: string; vmid: number; name: string; bebnowWKonfiguracji: number };
  bebny: {
    picNumber: number;
    modeType: number;
    konstrukcja: string | null;
    freeIssue: boolean;
    rodzaj: 'ROTO' | 'DYNABOX' | 'LOCKERBOX' | 'INNY';
    /** Poziomy, czyli szuflady w języku serwisanta; `null` = nie da się ustalić bez zgadywania. */
    poziomow: number | null;
    /** Kolumny logiczne; `null` = ta konstrukcja kolumn nie ma (LockerBox). */
    kolumn: number | null;
    /** Kolumny w ramie fizycznej; `null` = brak śladu poszerzenia. */
    kolumnFizycznych: number | null;
    wierszySzuflad: number;
    przegrod: number;
    przegrodFizycznych: number | null;
    przegrodDeklarowanych: number;
    typySzuflad: string[];
    kolumnDynaBox: number;
  }[];
};

function Liczba({ etykieta, wartosc, uwaga }: { etykieta: string; wartosc: string; uwaga?: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{etykieta}</p>
      <p className="font-mono text-sm">{wartosc}</p>
      {uwaga !== undefined && <p className="text-xs text-[var(--warning)]">{uwaga}</p>}
    </div>
  );
}

/**
 * BANER ROZJAZDU — przepisany 28.08.2026 po uwadze z wdrożenia: „to taka ściana tekstu i to
 * z tabelami, że ciężko się to czyta".
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ POPRZEDNIA WERSJA WYPISYWAŁA WSZYSTKIE LICZBY JAKO RÓWNORZĘDNE:
 *
 *      DrawerChannels: dynabox-sdi=104 · chmura-mssql-dbw=47 · sdi-bipoint=47 · temrexlockerbox=47
 *
 *  Osiem takich wierszy to trzydzieści dwie liczby, z których czytający sam miał wywnioskować,
 *  która maszyna ma prawdę i ilu wierszy brakuje pozostałym. Teraz ekran odpowiada na to sam:
 *  źródło POGRUBIONE i wymienione raz na górze, a w wierszu tabeli stoi tylko to, czego brakuje
 *  i komu.
 *
 *  ⚠️ SUROWE LICZBY ZOSTAJĄ, ale jako szczegół pod spodem — nie da się ich wyrzucić, bo po nich
 *  rozmawia się z serwisem i po nich poznać, czy wyrównanie zadziałało.
 * ════════════════════════════════════════════════════════════════════════════════════════════
 */
/**
 * CO SIĘ DZIEJE Z TYM AUTOMATEM — trzy stany, każdy o innym wniosku dla człowieka.
 *
 * ==============================================================================================
 *  W DRODZE     -> nic nie rob, portal czeka na wezel. To zarazem okno, w ktorym liczby na tej
 *                  karcie pokazuja wartosc PORTALU, a nie te z SPS (zamrozenie wiersza w locie).
 *  WSTRZYMANE   -> nic nie rob, ZROBI SIE SAMO po aktualizacji agenta. Nie jest to blad i nie
 *                  moze wygladac jak blad, bo operator zaczalby ratowac dzialajaca sytuacje.
 *  ODRZUCONE    -> trzeba czlowieka. Odmowa NIE WRACA do kolejki i nikt jej sam nie powtorzy.
 * ==============================================================================================
 *
 * ⚠️ TRZY OSOBNE SEKCJE, NIE JEDNA LISTA ZE STATUSEM. Wniosek jest inny w kazdym przypadku,
 * a wspolna lista kazalaby go wyczytac z kolumny — czyli przeniosla prace na czytajacego.
 */
function StanZmian({ dane }: { dane: Moduly }) {
  const { t } = useI18n();
  const z = dane.zmiany;
  if (z === undefined) return null;

  const wstrzymane = z.wDrodze.flatMap((o) =>
    o.wstrzymane.map((w) => ({ tytul: o.tytul, nodeId: w.nodeId, wymagana: w.wymagana })),
  );
  const czekajace = z.wDrodze.filter((o) => o.czekaja.length > 0);
  if (czekajace.length === 0 && wstrzymane.length === 0 && z.odrzucone.length === 0) return null;

  return (
    <div className="space-y-2 text-sm">
      {czekajace.length > 0 && (
        <div className="rounded-md border border-[var(--border)] bg-[var(--muted)] p-3">
          <p className="flex items-center gap-2 font-medium">
            <Clock className="size-4 shrink-0" />
            {t('moduly.wDrodze').replace('{ile}', String(czekajace.length))}
          </p>
          <ul className="mt-1 space-y-1 text-muted-foreground">
            {czekajace.map((o) => (
              <li key={o.opId}>
                {o.tytul} — {t('moduly.czekaNa')}{' '}
                <span className="font-mono">{o.czekaja.join(', ')}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {wstrzymane.length > 0 && (
        <div className="rounded-md border border-[var(--border)] bg-[var(--muted)] p-3">
          <p className="flex items-center gap-2 font-medium">
            <PauseCircle className="size-4 shrink-0" />
            {t('moduly.wstrzymane')}
          </p>
          <ul className="mt-1 space-y-1 text-muted-foreground">
            {wstrzymane.map((w, i) => (
              <li key={`${w.nodeId}-${i}`}>
                <span className="font-mono">{w.nodeId}</span>{' '}
                {t('moduly.wymagaWersji').replace('{wersja}', w.wymagana)}
              </li>
            ))}
          </ul>
          {/* Zdanie, ktore zdejmuje z operatora chec dzialania — to jest cala tresc tej sekcji. */}
          <p className="mt-1 text-xs text-muted-foreground">{t('moduly.wstrzymaneWyjasnienie')}</p>
        </div>
      )}

      {z.odrzucone.length > 0 && (
        <div className="rounded-md border border-[var(--destructive)] p-3">
          <p className="flex items-center gap-2 font-medium text-[var(--destructive)]">
            <AlertTriangle className="size-4 shrink-0" />
            {t('moduly.odrzucone').replace('{ile}', String(z.odrzucone.length))}
          </p>
          <ul className="mt-1 space-y-1">
            {z.odrzucone.map((o, i) => (
              <li key={`${o.opId}-${o.nodeId}-${i}`}>
                <span className="font-mono font-bold">{o.nodeId}</span>: {o.tytul}
                {/*
                  ⚠️ POWOD OD WEZLA, W CALOSCI I BEZ SKRACANIA. Wartownik pisze go zdaniem
                  („wiersz zmieniony poza portalem po ostatnim odczycie agenta") wlasnie po to,
                  zeby dalo sie go przeczytac tutaj i zrozumiec bez zagladania w logi.
                */}
                <span className="block text-muted-foreground">{o.powod}</span>
              </li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-muted-foreground">{t('moduly.odrzuconeWyjasnienie')}</p>
        </div>
      )}
    </div>
  );
}

function BanerRozjazdu({ dane }: { dane: Moduly }) {
  const { t } = useI18n();
  const [szczegoly, setSzczegoly] = useState(false);
  const stabilny = dane.rozjazdy.some((r) => r.stabilny);
  /**
   * ⚠️ WIEDZA PRZED DOMYSŁEM: `wlascicielWezel` przychodzi z API i jest faktem; `zrodloAutomatu`
   * zgaduje po liczbie wierszy i służy tylko starszym instancjom, które tego pola nie oddają.
   */
  const zrodlo = dane.wlascicielWezel ?? zrodloAutomatu(dane.rozjazdy);
  const zeZnanegoWlasciciela = dane.wlascicielWezel != null;

  return (
    <div
      className={
        stabilny
          ? 'rounded-md border border-[var(--destructive)]/40 bg-[var(--destructive)]/10 p-3 text-xs'
          : 'rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-3 text-xs'
      }
    >
      <p className="flex items-center gap-2 text-sm font-medium">
        <AlertTriangle className="size-4 shrink-0" />
        {stabilny
          ? t('moduly.rozjazdWezlow')
          : t('moduly.propagacjaWToku').replace('{minut}', String(dane.stabilneMinut))}
      </p>

      {/*
        ⚠️⚠️ ZDANIE, KTÓREGO BRAKOWAŁO NAJBARDZIEJ: skąd pójdą zmiany. Bez niego trzeba było
        porównać wzrokiem cztery liczby w każdym z ośmiu wierszy, żeby dojść do wniosku, który
        portal ma na wyciągnięcie ręki.
      */}
      {zrodlo !== null && (
        <p className="mt-2">
          {(zeZnanegoWlasciciela ? t('moduly.zrodloZmian') : t('moduly.zrodloDomysl')).split('{wezel}')[0]}
          <span className="font-mono font-bold">{zrodlo}</span>
          {(zeZnanegoWlasciciela ? t('moduly.zrodloZmian') : t('moduly.zrodloDomysl')).split('{wezel}')[1]}
        </p>
      )}

      {/*
        ⚠️ OZNACZENIE JEST PER WIERSZ, NIE PER BANER — poprawka po obejrzeniu tego ekranu na żywo.
        Jeden stabilny rozjazd malował cały baner na czerwono, a świeża różnica leżała na tej samej
        liście bez adnotacji — kto by ją przeczytał, poszedłby szukać usterki tam, gdzie zmiana
        po prostu jeszcze jedzie.
      */}
      <ul className="mt-2 space-y-1.5">
        {dane.rozjazdy.map((r) => {
          const o = opisRozjazdu(r.wezly, r.rodzaj, dane.wlascicielWezel);
          return (
            <li key={`${r.tabela}/${r.vmid}`} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="font-mono font-semibold">{r.tabela}</span>
              {o.rodzaj === 'TRESC' ? (
                <span>{t('moduly.innaTresc')}</span>
              ) : (
                <>
                  {/*
                    ⚠️ LICZBA BRAKUJĄCYCH WIERSZY, NIE DWIE LICZBY DO ODJĘCIA. „brakuje 57" można
                    przeczytać; „104 kontra 47" trzeba policzyć, i to osiem razy pod rząd.

                    ⚠️ I ZGRUPOWANE PO LICZBIE: na wyspie czterowęzłowej trzy węzły brakuje zwykle tyle
                    samo, bo są kopią tego samego obrazu wdrożeniowego. Osobny wpis na węzeł to
                    dwadzieścia cztery powtórzenia tej samej liczby na jednym ekranie.
                  */}
                  {/*
                    ⚠️⚠️ NADMIAR TO OSOBNA KATEGORIA, NIE „UJEMNY BRAK". Węzeł, który ma WIĘCEJ
                    wierszy niż właściciel, trzyma zjawę z chwili klonowania wyspy — a wyrównanie
                    jej NIE USUNIE, bo dosypuje brakujące i nigdy nie kasuje. Bez tego napisu ekran
                    obiecywałby sprzątnięcie, którego przycisk nie umie.
                  */}
                  {o.nadmiar.map((n) => (
                    <span key={`nad-${n.nodeId}`} className="text-[var(--warning)]">
                      {t('moduly.nadmiar').replace('{ile}', String(n.ponad)).replace('{wezel}', n.nodeId)}
                    </span>
                  ))}
                  {o.grupy.map((g) => (
                    <span key={g.brakuje} className="text-[var(--destructive)]">
                      {(g.wcale
                        ? t('moduly.brakWcaleGrupa')
                        : t('moduly.brakujeGrupa').replace('{ile}', String(g.brakuje))
                      ).replace('{wezly}', g.wezly.join(', '))}
                    </span>
                  ))}
                </>
              )}
              {/*
                ⚠️⚠️ TABELA, KTÓREJ PRZYCISK NIE RUSZA, MUSI BYĆ OZNACZONA. Baner wymienia osiem
                tabel, wyrównanie rozsyła sześć — bez tego napisu człowiek klika, widzi, że „dwie
                zostały", i uznaje, że przycisk nie działa.
              */}
              {dane.tabeleWyrownania !== undefined && !dane.tabeleWyrownania.includes(r.tabela) && (
                <span className="rounded border border-border px-1 text-[10px] text-muted-foreground">
                  {t('moduly.pozaWyrownaniem')}
                </span>
              )}
              {!r.stabilny && <span className="text-muted-foreground">— {t('moduly.dojezdza')}</span>}
            </li>
          );
        })}
      </ul>

      {/*
        ⚠️ SUROWE LICZBY POD PRZEŁĄCZNIKIEM, nie na wierzchu i nie usunięte. Na wierzchu były
        ścianą tekstu; usunięte zabrałyby jedyny sposób sprawdzenia, czy wyrównanie zadziałało,
        i jedyną postać, w której da się to podać serwisowi.
      */}
      <button
        type="button"
        className="mt-2 inline-flex items-center gap-1 underline decoration-dotted"
        onClick={() => setSzczegoly((v) => !v)}
      >
        {szczegoly ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
        {szczegoly ? t('moduly.ukryjLiczby') : t('moduly.pokazLiczby')}
      </button>
      {szczegoly && (
        <ul className="mt-1.5 space-y-0.5 font-mono text-[11px] text-muted-foreground">
          {dane.rozjazdy.map((r) => (
            <li key={`liczby/${r.tabela}/${r.vmid}`}>
              {r.tabela}: {r.wezly.map((w) => `${w.nodeId}=${w.wierszy}`).join(' · ')}
            </li>
          ))}
        </ul>
      )}

      {/*
        ⚠️ ZDANIE POJAWIA SIĘ TYLKO PRZY BRAKU CAŁKOWITYM, nie przy każdym rozjeździe — patrz
        `doWyrownania`. Kieruje do przycisku wyżej zamiast dubla przycisku tutaj: dwa te same
        przyciski na jednym ekranie każą się zastanawiać, czym się różnią.
      */}
      {dane.doWyrownania === true && <p className="mt-2">{t('moduly.mozeszWyrownac')}</p>}
    </div>
  );
}

export function ModulyAutomatu({
  machineId,
  groupId,
  nazwaMaszyny,
}: {
  machineId: string;
  groupId: string;
  nazwaMaszyny: string;
}) {
  const { t } = useI18n();
  const { isAdmin } = useAuth();
  const [nowyOtwarty, setNowyOtwarty] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();
  /**
   * ⚠️ PYTANIA W OKNIE PORTALU, nie w `confirm()` przeglądarki (zgłoszone 02.09.2026) — a tu
   * najbardziej: to jedyne dwa przyciski, które ZAPISUJĄ DO BAZ WSZYSTKICH WĘZŁÓW naraz,
   * a „Nie pozwalaj pytać ponownie" w oknie przeglądarki wyłączało oba pytania na trwałe.
   *
   * ⚠️ JEDEN STAN NA DWIE OPERACJE, bo wykluczają się wzajemnie — i dzięki temu nie da się
   * otworzyć obu pytań naraz nad tym samym automatem.
   *
   * ⚠️⚠️ HOOK STOI NAD WCZESNYMI `return`ami I MUSI TAM ZOSTAĆ. Postawiony niżej wykonywał się
   * dopiero po wczytaniu danych, więc pierwszy render (spinner) miał o jeden hook mniej niż
   * drugi — React przerywa wtedy całe drzewo i zakładka „Moduły" gasła na BIAŁY EKRAN po chwili
   * ładowania. Zgłoszone 02.09.2026, wprowadzone tego samego dnia razem z tym oknem.
   */
  const [doWyrownania, setDoWyrownania] = useState<'uzupelnij' | 'domknij' | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['machines', 'moduly', machineId, groupId],
    queryFn: () => apiGet<Moduly>(`/machines/${machineId}/moduly?groupId=${groupId}`),
  });

  const wyrownaj = useMutation({
    mutationFn: (wylacznosc: boolean) => wyrownajGeometrie(machineId, groupId, wylacznosc),
    onSuccess: (w) => {
      /**
       * ⚠️ MOWIMY O LICZBIE OPERACJI, NIE TYLKO ZMIAN. Wyrownanie idzie bebnami, wiec jedno
       * klikniecie zaklada kilka operacji — a w rejestrze zmian czlowiek zobaczy ich tyle samo
       * i musi wiedziec, ze to jego jedno klikniecie, a nie kilka przypadkow.
       */
      const ile = w.operacje?.length ?? 1;
      toast.success(
        t(ile > 1 ? 'moduly.wyrownanieZleconeBebny' : 'moduly.wyrownanieZlecone')
          .replace('{zmian}', String(w.zmian))
          .replace('{operacji}', String(ile)),
      );
      /** Bęben, który nie zmieścił się nawet osobno, MUSI wyjść osobnym ostrzeżeniem. */
      if (w.pominieteBebny !== undefined && w.pominieteBebny.length > 0) {
        toast.error(t('moduly.pominieteBebny').replace('{bebny}', w.pominieteBebny.join(', ')));
      }
      void qc.invalidateQueries({ queryKey: ['machines', 'moduly', machineId, groupId] });
      void qc.invalidateQueries({ queryKey: ['changes'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : t('common.error')),
  });

  if (isPending) {
    return (
      <p className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        {t('common.loading')}
      </p>
    );
  }
  if (error || !data) return <p className="py-6 text-sm text-muted-foreground">{t('common.error')}</p>;

  /**
   * ⚠️ BRAK POLA (instancja starsza od tej zmiany) ZNACZY „WOLNO", czyli zachowanie dotychczasowe:
   * odmowa przychodzi wtedy dopiero z API. Domyślne „nie wolno" wyszarzyłoby przyciski na każdej
   * instancji, która jeszcze nie ma tej wersji.
   */
  const bramka = data.wyrownanie ?? { mozna: true, powod: null };
  const mozeRozsylac = bramka.mozna;

  return (
    <div className="space-y-4">
      {doWyrownania !== null && (
        <OknoPotwierdzenia
          tytul={doWyrownania === 'domknij' ? t('moduly.domkniecie.nazwa') : t('moduly.wyrownaj')}
          tresc={t(doWyrownania === 'domknij' ? 'moduly.domkniecie.pytanie' : 'moduly.wyrownajPytanie').replace(
            '{nazwa}',
            nazwaMaszyny,
          )}
          /** ⚠️ DOMKNIĘCIE USUWA u rodzeństwa nadmiar razem ze stanem — i tego się nie cofa. */
          ton={doWyrownania === 'domknij' ? 'grozne' : 'zwykly'}
          etykietaPotwierdz={doWyrownania === 'domknij' ? t('moduly.domkniecie.nazwa') : t('moduly.wyrownaj')}
          pracuje={wyrownaj.isPending}
          onPotwierdz={() => {
            wyrownaj.mutate(doWyrownania === 'domknij');
            setDoWyrownania(null);
          }}
          onAnuluj={() => setDoWyrownania(null)}
        />
      )}
      {/*
        ⚠️ PRZYCISK TYLKO DLA ADMINA, tak samo jak `SPSConfig.XML` — ta operacja zapisuje
        konstrukcję maszyny, a nie dane o towarze. Uprawnienie punktowe na to nie wystarcza.
      */}
      {isAdmin && !mozeRozsylac && bramka.powod !== null && (
        /*
          ⚠️ POWÓD STOI NAD PRZYCISKAMI, NIE TYLKO W DYMKU. Dymka nikt nie szuka na wyszarzonym
          przycisku — a to jest ten moment, w którym człowiek ma zrobić coś INNEGO (zasiać mastera),
          zamiast czekać, aż przycisk zadziała.
        */
        <p className="flex items-start gap-2 rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2 text-xs">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-[var(--warning)]" />
          <span>{bramka.powod}</span>
        </p>
      )}
      {isAdmin && (
        <div className="flex flex-wrap justify-end gap-2">
          {/*
            ⚠️ PYTANIE PRZED WYRÓWNANIEM, tak samo jak przy odwoływaniu tokenów — i tu tym
            bardziej, bo to ZAPIS DO BAZ WSZYSTKICH WĘZŁÓW WYSPY naraz. Pytanie musi powiedzieć
            dwie rzeczy, których inaczej nikt nie zgadnie: że węzły mające te wiersze pominą je
            same, i że rozjazdu treści to NIE naprawia.
          */}
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={wyrownaj.isPending || !mozeRozsylac}
            /**
             * ⚠️ DYMEK MÓWI, CO PRZYCISK ROBI, a nie jak się nazywa — zgłoszone wprost
             * (29.08.2026): „kto będzie wiedział, co robi «Wyrównanie z domknięciem»". Nazwa
             * musi być krótka, więc różnicę między dwoma przyciskami niesie dopiero to zdanie.
             *
             * ⚠️ PRZY ZABLOKOWANYM PRZYCISKU DYMEK MÓWI DLACZEGO. Wyszarzenie bez powodu jest
             * gorsze niż odmowa po kliknięciu: nie ma z niego jak wyjść.
             */
            title={bramka.powod ?? t('moduly.wyrownajDymek')}
            onClick={() => setDoWyrownania('uzupelnij')}
          >
            {wyrownaj.isPending ? <Loader2 className="size-4 animate-spin" /> : <Rows3 className="size-4" />}
            {t('moduly.wyrownaj')}
          </Button>
          {/*
            DOMKNIECIE — OSOBNY PRZYCISK I OSOBNE PYTANIE, i to nie jest kosmetyka.

            ⚠️⚠️ WYROWNANIE TYLKO DOSYPUJE i jest bezpieczne z definicji; domkniecie USUWA
            u rodzenstwa wszystko, czego nie ma u wlasciciela, RAZEM ZE STANEM MAGAZYNOWYM.
            Schowanie tego pod jednym przyciskiem z checkboxem znaczyloby, ze dwie operacje
            o roznym ryzyku wygladaja tak samo w chwili klikniecia.

            ⚠️ WARIANT NISZCZACY JEST `outline`, NIE `destructive` — czerwony przycisk obok
            zwyklego uczy oko, ze to ten „mocniejszy" wariant tego samego, a nie inna operacja.
            Rozroznienie niesie TEKST PYTANIA, ktore trzeba przeczytac, zeby je potwierdzic.
          */}
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={wyrownaj.isPending || !mozeRozsylac}
            /** ⚠️ Ten dymek MUSI nazwać skutek nieodwracalny — patrz komentarz przy pierwszym. */
            title={bramka.powod ?? t('moduly.domkniecieDymek')}
            onClick={() => setDoWyrownania('domknij')}
          >
            {wyrownaj.isPending ? <Loader2 className="size-4 animate-spin" /> : <Eraser className="size-4" />}
            {t('moduly.domkniecie.nazwa')}
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => setNowyOtwarty(true)}>
            <Plus className="size-4" />
            {t('beben.dodaj')}
          </Button>
        </div>
      )}
      {nowyOtwarty && (
        <NowyBebenDialog
          maszyna={{ id: machineId, name: nazwaMaszyny }}
          groupId={groupId}
          onClose={() => setNowyOtwarty(false)}
        />
      )}
      {/*
        ⚠️ ROZJAZD MIĘDZY MASZYNAMI STOI NAJWYŻEJ, bo unieważnia wszystko poniżej. Liczby niżej
        opisują to, co widzi maszyna-właściciel; jeśli pozostałe maszyny wyspy widzą coś innego,
        to najpierw trzeba wiedzieć TO, a potem czytać szczegóły.

        ⚠️ „W trakcie propagacji" i „rozjazd" to dwa różne komunikaty. Operacja config-down
        dojeżdża do węzłów po kolei, więc świeża różnica jest NORMALNA — nazwanie jej rozjazdem
        uczyłoby ludzi ignorować ten baner.
      */}
      <StanZmian dane={data} />
      {data.rozjazdy.length > 0 && <BanerRozjazdu dane={data} />}

      {/*
        ⚠️ ROZJAZD „ile bębnów mówi konfiguracja" kontra „ile portal zna" JEST TREŚCIĄ, nie
        szczegółem. Bęben, o którym konfiguracja wie, a portal nie, znaczy niepełną synchronizację
        — i to jest pierwsza rzecz, którą trzeba zobaczyć, zanim się uwierzy resztcie liczb.
      */}
      {data.automat.bebnowWKonfiguracji !== data.bebny.length && (
        <p className="rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-3 text-xs">
          {t('moduly.rozjazd')
            .replace('{konfiguracja}', String(data.automat.bebnowWKonfiguracji))
            .replace('{portal}', String(data.bebny.length))}
        </p>
      )}

      {data.bebny.length === 0 ? (
        <p className="py-6 text-sm text-muted-foreground">{t('moduly.brak')}</p>
      ) : (
        data.bebny.map((b) => {
          const brakuje = b.przegrodDeklarowanych - b.przegrod;
          return (
            <div key={b.picNumber} className="rounded-lg border p-4">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Boxes className="size-4 text-muted-foreground" />
                <span className="font-medium">
                  {t('moduly.pic')} {b.picNumber}
                </span>
                {/* Nazwa jest nasza, numer jest prawdą — dlatego oba, zawsze razem. */}
                <Badge variant={b.konstrukcja === null ? 'neutral' : 'default'}>
                  {b.konstrukcja ?? t('moduly.nieznanaKonstrukcja')}
                  <span className="font-mono opacity-70">MODETYPE {b.modeType}</span>
                </Badge>
                {b.freeIssue && <Badge variant="warning">{t('moduly.swobodne')}</Badge>}
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {/*
                  ⚠️⚠️ NAZWY W BAZIE NIE SĄ NAZWAMI Z MASZYNY, i to była treść zgłoszenia
                  (01.09.2026): karta pisała „Szuflad 35", a 35 to liczba KOLUMN. Poprawione
                  po korekcie użytkownika: „BiPoint to też bęben i ma szuflady/kolumny tak samo
                  jak dynabox i roto, tylko lockerbox ma same szuflady". Skąd które liczby biorą
                  się w SPS — zmierzone i wypisane w `api/src/machines/geometria-bebna.ts`.

                  ⚠️ LICZBA W NAWIASIE = RAMA FIZYCZNA. Pokazujemy ją tylko wtedy, gdy portal ma
                  na nią dowód (dziura w numeracji), nigdy „na wszelki wypadek".
                */}
                {b.poziomow !== null && <Liczba etykieta={t('moduly.szuflad')} wartosc={String(b.poziomow)} />}
                {b.kolumn === null ? (
                  /** LockerBox: kolumn nie ma, a wiersz `Drawers` jest szufladą — tak to pokazujemy. */
                  <Liczba etykieta={t('moduly.szuflad')} wartosc={String(b.wierszySzuflad)} />
                ) : (
                  <Liczba
                    etykieta={t('moduly.kolumnRoto')}
                    wartosc={
                      b.kolumnFizycznych === null ? String(b.kolumn) : `${b.kolumn} (${b.kolumnFizycznych})`
                    }
                  />
                )}
                <Liczba
                  etykieta={t('moduly.przegrod')}
                  wartosc={
                    b.przegrodFizycznych === null
                      ? String(b.przegrod)
                      : `${b.przegrod} (${b.przegrodFizycznych})`
                  }
                  {...(brakuje !== 0
                    ? { uwaga: t('moduly.deklarowanych').replace('{n}', String(b.przegrodDeklarowanych)) }
                    : {})}
                />
                <Liczba
                  etykieta={t('moduly.typSzuflady')}
                  wartosc={b.typySzuflad.length === 0 ? '—' : b.typySzuflad.join(', ')}
                />
                {/*
                  ⚠️ OSOBNA KRATKA „Kolumn DynaBox" ZNIKA, bo od tej zmiany kolumny DynaBoksa stoją
                  w kratce „Kolumn" razem z ramą fizyczną — dwie kratki o tej samej liczbie kazałyby
                  szukać różnicy, której nie ma.
                */}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
